import React, { useState } from "react";
import {
  GraduationCap,
  Plus,
  X,
  Edit2,
  Trash2,
  Copy,
  CopyPlus,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ArrowUpDown,
  Power,
  Calendar,
  Search,
} from "lucide-react";
import { formatDisplayDate } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";
import { renderMarkdownContent, MARKDOWN_HINT } from "../utils/markdownContent";
import {
  PAPER_TYPES,
  paperTypeLabel,
  deriveEntryStatus,
  cardIsDead,
  isEntryLive,
  isEntryEmpty,
  sortCards,
  groupCardsByStatus,
  getCardDisplayStatus,
  cardToText,
  newEntry,
  newConferenceCard,
  collectAllFieldTags,
  MANUAL_STATUS_KEYS,
  STATUS,
} from "../utils/conferences";

const normalizeUrl = (raw) => {
  const url = (raw || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const SORT_MODES = [
  { id: "deadline", label: "Submission deadline" },
  { id: "date", label: "Conference date" },
  { id: "field", label: "Field" },
  { id: "added", label: "Date added" },
  { id: "modified", label: "Date modified" },
  { id: "livedead", label: "Live / Dead" },
];

const FieldTagsInput = ({ tags = [], suggestions = [], onChange, inputCls }) => {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const normalized = tags.map((t) => String(t || "").trim()).filter(Boolean);
  const q = input.trim().toLowerCase();
  const filtered = suggestions.filter(
    (s) =>
      s.toLowerCase().includes(q) &&
      !normalized.some((t) => t.toLowerCase() === s.toLowerCase())
  );

  const addTag = (raw) => {
    const tag = String(raw || "").trim();
    if (!tag) return;
    if (normalized.some((t) => t.toLowerCase() === tag.toLowerCase())) return;
    onChange([...normalized, tag]);
    setInput("");
    setShowSuggestions(false);
  };

  const removeTag = (idx) => onChange(normalized.filter((_, i) => i !== idx));

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {normalized.map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            className="inline-flex items-center gap-1 text-xs bg-amber-500/15 text-amber-200 border border-amber-500/30 px-2 py-0.5 rounded-full"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(idx)}
              className="text-amber-300/70 hover:text-amber-100"
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addTag(input);
          } else if (e.key === "Backspace" && !input && normalized.length) {
            removeTag(normalized.length - 1);
          }
        }}
        placeholder="Type a field tag and press Enter"
        className={inputCls}
      />
      {showSuggestions && q && filtered.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-36 overflow-y-auto">
          {filtered.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addTag(s)}
              className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Conferences = ({
  cards = [],
  sortMode = "deadline",
  onSortModeChange,
  onAddCard,
  onUpdateCard,
  onDeleteCard,
  onDeleteAllCards,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  const setSortMode = onSortModeChange || (() => {});
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [showAddCard, setShowAddCard] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [expanded, setExpanded] = useState({});
  const [entryIndex, setEntryIndex] = useState({}); // cardId -> current entry idx
  const [editing, setEditing] = useState(null); // { cardId, entryId }
  const [draft, setDraft] = useState(null); // entry draft + _title

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const currentIdx = (card) => {
    const len = (card.entries || []).length;
    const raw = entryIndex[card.id] ?? 0;
    return Math.min(Math.max(raw, 0), Math.max(len - 1, 0));
  };

  const setIdx = (cardId, idx) =>
    setEntryIndex((prev) => ({ ...prev, [cardId]: idx }));

  const patchEntry = (card, entryId, patch) => {
    const entries = (card.entries || []).map((e) =>
      e.id === entryId ? { ...e, ...patch } : e
    );
    onUpdateCard(card.id, { entries });
  };

  // ---- add card ----
  const handleAddCard = () => {
    const title = newTitle.trim();
    if (!title) return;
    const card = newConferenceCard(title);
    onAddCard(card);
    setNewTitle("");
    setShowAddCard(false);
    setExpanded((prev) => ({ ...prev, [card.id]: true }));
  };

  // ---- entry navigation / management ----
  const goPrev = (card) => {
    const idx = currentIdx(card);
    if (idx > 0) setIdx(card.id, idx - 1);
  };
  const goNext = (card) => {
    const idx = currentIdx(card);
    if (idx < (card.entries || []).length - 1) setIdx(card.id, idx + 1);
  };

  const addEntry = (card) => {
    const entries = card.entries || [];
    const lastYear = entries.length
      ? parseInt(entries[entries.length - 1].year, 10)
      : new Date().getFullYear();
    const nextYear = Number.isNaN(lastYear)
      ? new Date().getFullYear()
      : lastYear + 1;
    const entry = newEntry(nextYear);
    const updated = [...entries, entry];
    onUpdateCard(card.id, { entries: updated });
    setIdx(card.id, updated.length - 1);
    setExpanded((prev) => ({ ...prev, [card.id]: true }));
    startEdit(card.id, entry, card);
  };

  const deleteEntry = (card, entryId) => {
    const entries = (card.entries || []).filter((e) => e.id !== entryId);
    if (entries.length === 0) {
      // Don't leave a card with zero entries; delete the whole card instead.
      if (window.confirm(`This is the last edition. Delete the whole "${card.title}" card?`)) {
        onDeleteCard(card.id);
      }
      return;
    }
    onUpdateCard(card.id, { entries });
    setIdx(card.id, Math.max(currentIdx(card) - 1, 0));
  };

  // ---- duplicate / copy ----
  const duplicateCard = (card) => {
    const clone = {
      ...newConferenceCard(`${card.title} (copy)`),
      organizers: card.organizers || "",
      titleExpanded: card.titleExpanded || "",
      entries: (card.entries || []).map((e) => ({
        ...newEntry(e.year),
        ...e,
        id: Date.now() + Math.floor(Math.random() * 100000),
      })),
    };
    onAddCard(clone);
  };

  const copyCardText = (card) => {
    navigator.clipboard.writeText(cardToText(card));
  };

  // ---- edit entry ----
  const startEdit = (cardId, entry, card) => {
    setEditing({ cardId, entryId: entry.id });
    setDraft({
      ...entry,
      _title: card.title,
      _organizers: card.organizers || "",
      _titleExpanded: card.titleExpanded || "",
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
  };
  const saveEdit = (card) => {
    if (!draft) return;
    const { _title, _organizers, _titleExpanded, ...entry } = draft;
    const entries = (card.entries || []).map((e) =>
      e.id === entry.id ? entry : e
    );
    onUpdateCard(card.id, {
      entries,
      title: (_title || "").trim() || card.title,
      organizers: (_organizers || "").trim(),
      titleExpanded: (_titleExpanded || "").trim(),
    });
    cancelEdit();
  };
  const updateDraft = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  // ---- status quick actions (read view) ----
  const toggleLive = (card, entry) =>
    patchEntry(card, entry.id, { live: !isEntryLive(entry) });
  const toggleRemember = (card, entry) =>
    patchEntry(card, entry.id, { remember: !entry.remember });
  const markSubmitted = (card, entry) =>
    patchEntry(card, entry.id, { submitted: true });
  const unmarkSubmitted = (card, entry) =>
    patchEntry(card, entry.id, { submitted: false, outcome: "pending" });
  const setOutcome = (card, entry, outcome) =>
    patchEntry(card, entry.id, { outcome });
  const setDecisionDate = (card, entry, date) =>
    patchEntry(card, entry.id, { decisionDate: date || null });

  // ---- filtering / sorting for display ----
  const matchesSearch = (card) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if ((card.title || "").toLowerCase().includes(q)) return true;
    if ((card.organizers || "").toLowerCase().includes(q)) return true;
    if ((card.titleExpanded || "").toLowerCase().includes(q)) return true;
    return (card.entries || []).some(
      (e) =>
        (e.venue || "").toLowerCase().includes(q) ||
        (e.paperName || "").toLowerCase().includes(q) ||
        (e.authors || "").toLowerCase().includes(q) ||
        (e.theme || "").toLowerCase().includes(q) ||
        (e.fields || []).some((f) => (f || "").toLowerCase().includes(q)) ||
        String(e.year || "").includes(q)
    );
  };

  const allFieldTags = collectAllFieldTags(cards);
  const visibleCards = sortCards(cards.filter(matchesSearch), sortMode);
  const cardGroups = groupCardsByStatus(visibleCards, entryIndex);

  // ---- edit form ----
  const renderEditForm = (card, entry) => {
    const d = draft;
    if (!d) return null;
    const dlLabel = `${paperTypeLabel(d) || "Submission"} deadline`;
    const inputCls =
      "w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-amber-500 outline-none text-sm";
    return (
      <div className="space-y-3 p-3 bg-gray-700/40 rounded-xl border-2 border-amber-500/50">
        <div>
          <label className="block text-xs text-amber-300 mb-1">Conference name (short)</label>
          <input
            type="text"
            value={d._title ?? ""}
            onChange={(e) => updateDraft("_title", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Organizers</label>
          <input
            type="text"
            value={d._organizers ?? ""}
            onChange={(e) => updateDraft("_organizers", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Title (expanded)</label>
          <input
            type="text"
            value={d._titleExpanded ?? ""}
            onChange={(e) => updateDraft("_titleExpanded", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Theme</label>
          <input
            type="text"
            value={d.theme ?? ""}
            onChange={(e) => updateDraft("theme", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Field</label>
          <FieldTagsInput
            tags={d.fields || []}
            suggestions={allFieldTags}
            onChange={(fields) => updateDraft("fields", fields)}
            inputCls={inputCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-amber-300 mb-1">Year</label>
            <input
              type="text"
              inputMode="numeric"
              value={d.year ?? ""}
              onChange={(e) => updateDraft("year", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-amber-300 mb-1">Venue</label>
            <input
              type="text"
              value={d.venue ?? ""}
              onChange={(e) => updateDraft("venue", e.target.value)}
              placeholder="City, Country"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-amber-300 mb-1">Date from</label>
            <input
              type="date"
              value={d.dateFrom || ""}
              onChange={(e) => updateDraft("dateFrom", e.target.value || null)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-amber-300 mb-1">Date to</label>
            <input
              type="date"
              value={d.dateTo || ""}
              onChange={(e) => updateDraft("dateTo", e.target.value || null)}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Website</label>
          <input
            type="text"
            value={d.website ?? ""}
            onChange={(e) => updateDraft("website", e.target.value)}
            placeholder="https://conference.org"
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-amber-300 mb-1">Paper name</label>
            <input
              type="text"
              value={d.paperName ?? ""}
              onChange={(e) => updateDraft("paperName", e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-amber-300 mb-1">Paper type</label>
            <select
              value={d.paperType || "full"}
              onChange={(e) => updateDraft("paperType", e.target.value)}
              className={inputCls}
            >
              {PAPER_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {d.paperType === "other" && (
          <input
            type="text"
            value={d.paperTypeOther ?? ""}
            onChange={(e) => updateDraft("paperTypeOther", e.target.value)}
            placeholder="Custom paper type label"
            className={inputCls}
          />
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-amber-300 mb-1">{dlLabel}</label>
            <input
              type="date"
              value={d.submissionDeadline || ""}
              onChange={(e) =>
                updateDraft("submissionDeadline", e.target.value || null)
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-amber-300 mb-1">Status</label>
            <select
              value={d.statusOverride || ""}
              onChange={(e) =>
                updateDraft("statusOverride", e.target.value || null)
              }
              className={inputCls}
            >
              <option value="">Auto</option>
              {MANUAL_STATUS_KEYS.map((k) => (
                <option key={k} value={k}>
                  {STATUS[k].label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Authors</label>
          <input
            type="text"
            value={d.authors ?? ""}
            onChange={(e) => updateDraft("authors", e.target.value)}
            placeholder="Comma-separated"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs text-amber-300 mb-1">Add-on details</label>
          <textarea
            value={d.details ?? ""}
            onChange={(e) => updateDraft("details", e.target.value)}
            rows={4}
            placeholder={MARKDOWN_HINT}
            className={`${inputCls} resize-y font-mono`}
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={isEntryLive(d)}
              onChange={(e) => updateDraft("live", e.target.checked)}
              className="accent-amber-500"
            />
            Live edition (uncheck to mark as a Dead placeholder)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={!!d.remember}
              onChange={(e) => updateDraft("remember", e.target.checked)}
              className="accent-indigo-500"
            />
            Remember (uses submission deadline as due date)
          </label>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => saveEdit(card)}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Save edition
          </button>
          <button
            onClick={cancelEdit}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ---- read view of the current entry ----
  const renderEntryRead = (card, entry) => {
    const status = deriveEntryStatus(entry);
    const live = isEntryLive(entry);
    const url = normalizeUrl(entry.website);
    return (
      <div className="space-y-3">
        {/* Status + quick actions */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium border ${status.badge}`}
          >
            {status.label}
          </span>
          <button
            onClick={() => toggleLive(card, entry)}
            className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${
              live
                ? "bg-green-500/15 text-green-300 border-green-500/40 hover:bg-green-500/25"
                : "bg-gray-600/30 text-gray-400 border-gray-600/50 hover:bg-gray-600/50"
            }`}
            title={live ? "Mark this edition Dead" : "Mark this edition Live"}
          >
            <Power className="w-3 h-3" />
            {live ? "Live" : "Dead"}
          </button>
          <label
            className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1.5 cursor-pointer transition-colors ${
              entry.remember
                ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/40"
                : "bg-gray-600/30 text-gray-400 border-gray-600/50 hover:bg-gray-600/50"
            }`}
            title="Show in Remember (uses submission deadline as due date if set)"
          >
            <input
              type="checkbox"
              checked={!!entry.remember}
              onChange={() => toggleRemember(card, entry)}
              className="accent-indigo-500"
            />
            Remember
          </label>
          {live && !entry.submitted && (
            <button
              onClick={() => markSubmitted(card, entry)}
              className="text-xs px-2 py-0.5 rounded-full border bg-blue-500/15 text-blue-300 border-blue-500/40 hover:bg-blue-500/25 transition-colors"
            >
              Mark submitted
            </button>
          )}
          {entry.submitted && entry.outcome === "pending" && (
            <>
              <button
                onClick={() => setOutcome(card, entry, "accepted")}
                className="text-xs px-2 py-0.5 rounded-full border bg-green-500/15 text-green-300 border-green-500/40 hover:bg-green-500/25 transition-colors"
              >
                Accepted
              </button>
              <button
                onClick={() => setOutcome(card, entry, "rejected")}
                className="text-xs px-2 py-0.5 rounded-full border bg-red-500/15 text-red-300 border-red-500/40 hover:bg-red-500/25 transition-colors"
              >
                Rejected
              </button>
              <button
                onClick={() => unmarkSubmitted(card, entry)}
                className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
                title="Undo submitted"
              >
                undo
              </button>
            </>
          )}
          {entry.submitted && entry.outcome !== "pending" && (
            <button
              onClick={() => setOutcome(card, entry, "pending")}
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
              title="Reset decision"
            >
              reset decision
            </button>
          )}
        </div>

        {/* Decision date (when submitted & pending) */}
        {entry.submitted && entry.outcome === "pending" && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-indigo-300 flex-shrink-0" />
            <span className="text-gray-400">Decision date:</span>
            <input
              type="date"
              value={entry.decisionDate || ""}
              onChange={(e) => setDecisionDate(card, entry, e.target.value)}
              className="bg-gray-700 text-cyan-200 px-2 py-1 rounded-lg border border-gray-600 focus:border-indigo-500 outline-none text-sm"
            />
            {entry.decisionDate && (
              <span className="text-xs text-indigo-300/80">
                (shows in Remember)
              </span>
            )}
          </div>
        )}

        {/* Detail rows */}
        <dl className="text-sm space-y-1">
          {entry.theme && <Row label="Theme">{entry.theme}</Row>}
          {(entry.fields || []).length > 0 && (
            <Row label="Field">
              <span className="inline-flex flex-wrap gap-1">
                {entry.fields.map((tag, idx) => (
                  <span
                    key={`${tag}-${idx}`}
                    className="text-xs bg-amber-500/15 text-amber-200 border border-amber-500/30 px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </span>
            </Row>
          )}
          {(entry.dateFrom || entry.dateTo) && (
            <Row label="Dates">
              {entry.dateFrom ? formatDisplayDate(new Date(entry.dateFrom)) : "?"}
              {entry.dateTo ? ` – ${formatDisplayDate(new Date(entry.dateTo))}` : ""}
            </Row>
          )}
          {entry.venue && <Row label="Venue">{entry.venue}</Row>}
          {entry.website && (
            <Row label="Website">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-300 hover:underline inline-flex items-center gap-1 break-all"
              >
                {entry.website}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </Row>
          )}
          {entry.paperName && (
            <Row label="Paper">
              {entry.paperName}{" "}
              <span className="text-amber-300/80">({paperTypeLabel(entry)})</span>
            </Row>
          )}
          {entry.submissionDeadline && (
            <Row label={`${paperTypeLabel(entry)} deadline`}>
              <span className="text-red-300">
                {formatDisplayDate(new Date(entry.submissionDeadline))}
              </span>
            </Row>
          )}
          {entry.authors && <Row label="Authors">{renderRichText(entry.authors, `auth-${entry.id}`)}</Row>}
        </dl>

        {/* Add-on details */}
        {(entry.details || "").trim() && (
          <div className="bg-gray-800/50 rounded-lg p-2 max-h-48 overflow-y-auto border border-gray-700">
            {renderMarkdownContent(entry.details, `conf-${entry.id}`)}
          </div>
        )}

        {isEntryEmpty(entry) && live && (
          <p className="text-xs text-gray-500 italic">
            This edition is empty — add details, or mark it Dead.
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => startEdit(card.id, entry, card)}
            className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-amber-300 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" /> Edit edition
          </button>
          <button
            onClick={() => deleteEntry(card, entry.id)}
            className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 text-red-300 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete edition
          </button>
        </div>
      </div>
    );
  };

  // ---- single card ----
  const renderCard = (card) => {
    const entries = card.entries || [];
    const idx = currentIdx(card);
    const entry = entries[idx];
    const isOpen = expanded[card.id];
    const dead = cardIsDead(card);
    const isEditingThis =
      editing && editing.cardId === card.id && entry && editing.entryId === entry.id;
    const headStatus = entry
      ? getCardDisplayStatus(card, idx)
      : STATUS.start;

    return (
      <div
        key={card.id}
        tabIndex={0}
        onKeyDown={(e) => {
          if (isEditingThis) return;
          if (e.key === "ArrowLeft") {
            goPrev(card);
          } else if (e.key === "ArrowRight") {
            goNext(card);
          }
        }}
        className={`rounded-xl border-2 transition-all overflow-hidden outline-none focus:ring-2 focus:ring-amber-500/40 ${
          dead
            ? "bg-gray-800/40 border-gray-700"
            : "bg-gray-700/50 border-gray-600 hover:border-amber-500/50"
        }`}
      >
        {/* Header */}
        <div className="flex items-start gap-2 p-3">
          <button
            onClick={() => toggleExpand(card.id)}
            className="text-gray-400 hover:text-amber-400 transition-colors mt-0.5 flex-shrink-0"
            title={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4
                className={`font-semibold break-words ${
                  dead ? "text-gray-400" : "text-amber-300"
                }`}
              >
                {card.title}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium border ${headStatus.badge}`}
              >
                {headStatus.label}
              </span>
            </div>
            {/* Marquee nav */}
            {entries.length > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={() => goPrev(card)}
                  disabled={idx === 0}
                  className="text-gray-400 hover:text-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous edition"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-300 font-medium tabular-nums">
                  {entry ? entry.year : "—"}{" "}
                  <span className="text-gray-500">
                    ({idx + 1} of {entries.length})
                  </span>
                </span>
                <button
                  onClick={() => goNext(card)}
                  disabled={idx >= entries.length - 1}
                  className="text-gray-400 hover:text-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next edition"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Collapsed preview: key dates & location for the current edition */}
            {!isOpen && entry && (
              <CollapsedEntrySummary entry={entry} dead={dead} />
            )}
          </div>
          {/* Card actions */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => addEntry(card)}
              className="text-gray-400 hover:text-amber-400 transition-colors p-1"
              title="Add next edition (new year)"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => duplicateCard(card)}
              className="text-gray-400 hover:text-cyan-400 transition-colors p-1"
              title="Duplicate this conference"
            >
              <CopyPlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => copyCardText(card)}
              className="text-gray-400 hover:text-cyan-400 transition-colors p-1"
              title="Copy card details as text"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete conference "${card.title}" and all its editions?`)) {
                  onDeleteCard(card.id);
                }
              }}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Delete card"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        {isOpen && entry && (
          <div className="px-3 pb-3 pl-9">
            {((card.organizers || "").trim() || (card.titleExpanded || "").trim()) && (
              <dl className="text-sm space-y-1 mb-3 pb-3 border-b border-gray-600/60">
                {(card.organizers || "").trim() && (
                  <Row label="Organizers">{card.organizers}</Row>
                )}
                {(card.titleExpanded || "").trim() && (
                  <Row label="Title">{card.titleExpanded}</Row>
                )}
              </dl>
            )}
            {isEditingThis ? renderEditForm(card, entry) : renderEntryRead(card, entry)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-amber-500/30 shadow-lg">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <GraduationCap className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-amber-400 truncate">Conferences</h3>
          <span className="text-xs text-gray-500">({cards.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-amber-300" />
              </button>
              <button
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-amber-300" />
              </button>
            </div>
          )}

          {/* Sort */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu((v) => !v)}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors flex items-center gap-1"
              title="Sort cards"
            >
              <ArrowUpDown className="w-4 h-4 text-amber-300" />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-10 z-30 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[12rem]">
                <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wide">
                  Sort by
                </div>
                {SORT_MODES.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setSortMode(m.id);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      sortMode === m.id
                        ? "bg-amber-500/20 text-amber-300"
                        : "text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
                <div className="px-3 py-1 text-[11px] text-gray-500 border-t border-gray-700 mt-1">
                  Dead cards always sink to the bottom.
                </div>
              </div>
            )}
          </div>

          {cards.length > 0 && onDeleteAllCards && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Delete all ${cards.length} conference cards? This cannot be undone.`
                  )
                ) {
                  onDeleteAllCards();
                }
              }}
              className="p-2 bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 rounded-xl transition-colors"
              title="Delete all cards"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
            </button>
          )}
          <button
            onClick={() => {
              setShowAddCard((v) => !v);
              setNewTitle("");
            }}
            className="p-2 bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors"
            title="Add new conference"
          >
            {showAddCard ? (
              <X className="w-5 h-5 text-black" />
            ) : (
              <Plus className="w-5 h-5 text-black" />
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      {cards.length > 0 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, venue, paper, author, year..."
            className="w-full bg-gray-700 text-cyan-200 pl-9 pr-3 py-2 rounded-lg border-2 border-gray-600 focus:border-amber-500 outline-none text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-amber-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Add form */}
      {showAddCard && (
        <div className="mb-4 p-4 bg-gray-700/50 rounded-xl border-2 border-amber-500/50 space-y-3">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddCard();
              else if (e.key === "Escape") setShowAddCard(false);
            }}
            placeholder="Conference name (e.g. NeurIPS, ICML)..."
            autoFocus
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-amber-500 outline-none"
          />
          <p className="text-xs text-gray-400">
            Just the name to start — you can add the year's dates, venue, paper
            and deadline after it's created.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleAddCard}
              disabled={!newTitle.trim()}
              className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Add Conference
            </button>
            <button
              onClick={() => setShowAddCard(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {cards.length === 0 && !showAddCard && (
        <div className="text-center py-8 text-gray-400">
          <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>
            No conferences yet. Click the + button to track your first one.
          </p>
        </div>
      )}

      {cards.length > 0 && visibleCards.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm">
          No conferences match "{search}".
        </div>
      )}

      {/* Cards grouped by status */}
      {visibleCards.length > 0 && (
        <div className="space-y-8">
          {cardGroups.map(({ status, cards: groupCards }) => (
            <section key={status.key} aria-label={status.label}>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-semibold border flex-shrink-0 ${status.badge}`}
                >
                  {status.label}
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-gray-600 to-transparent" />
                <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">
                  {groupCards.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
                {groupCards.map(renderCard)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const CollapsedEntrySummary = ({ entry, dead }) => {
  const url = normalizeUrl(entry.website);
  const hasDeadline = !!entry.submissionDeadline;
  const hasDates = !!(entry.dateFrom || entry.dateTo);
  const hasVenue = !!(entry.venue || "").trim();
  const hasWebsite = !!(entry.website || "").trim();
  if (!hasDeadline && !hasDates && !hasVenue && !hasWebsite) return null;

  const muted = dead ? "text-gray-500" : "text-gray-400";
  const value = dead ? "text-gray-500" : "text-gray-300";

  return (
    <dl className={`mt-2 text-xs space-y-0.5 ${muted}`}>
      {hasDeadline && (
        <CompactRow label={`${paperTypeLabel(entry) || "Submission"} deadline`}>
          <span className={dead ? "text-gray-500" : "text-red-300/90"}>
            {formatDisplayDate(new Date(entry.submissionDeadline))}
          </span>
        </CompactRow>
      )}
      {hasDates && (
        <CompactRow label="Dates">
          <span className={value}>
            {entry.dateFrom ? formatDisplayDate(new Date(entry.dateFrom)) : "?"}
            {entry.dateTo ? ` – ${formatDisplayDate(new Date(entry.dateTo))}` : ""}
          </span>
        </CompactRow>
      )}
      {hasVenue && (
        <CompactRow label="Venue">
          <span className={value}>{entry.venue}</span>
        </CompactRow>
      )}
      {hasWebsite && (
        <CompactRow label="Website">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1 break-all hover:underline ${
              dead ? "text-gray-500" : "text-cyan-300/90"
            }`}
          >
            <span className="truncate max-w-[14rem] sm:max-w-xs">{entry.website}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        </CompactRow>
      )}
    </dl>
  );
};

const CompactRow = ({ label, children }) => (
  <div className="flex gap-2 min-w-0">
    <dt className="flex-shrink-0 w-[5.5rem] sm:w-28 truncate">{label}</dt>
    <dd className="min-w-0 break-words">{children}</dd>
  </div>
);

// Small label/value row used in the read view.
const Row = ({ label, children }) => (
  <div className="flex gap-2">
    <dt className="text-gray-500 flex-shrink-0 w-28">{label}</dt>
    <dd className="text-gray-300 break-words min-w-0">{children}</dd>
  </div>
);

export default Conferences;
